import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Trash2, Building2, Globe, Database, ArrowLeft, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAssignments, type RoleAssignment } from '@/store/assignment';
import { useResources } from '@/store/resource';
import { useLayout } from '@/contexts/layout-context';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/loader';
import { useUsers } from '@/store/user';
import { useGroups } from '@/store/group';
import { useTenants } from '@/store/tenant';
import { useRoles } from '@/store/role';
import { PageLayout } from '@/components/page-layout';

interface GroupedPrincipalRow {
    principalId: string;
    principalType: string;
    name: string;
    subtext: string;
    targetUserId: string | null; // For navigation
    tenants: Array<{ id: string; name: string; logo?: string }>;
    scopes: Array<{ id: string; name: string; logo?: string; isApp?: boolean }>;
    roles: Array<{ id: string; name: string }>;
    assignments: RoleAssignment[];
}

export const AssignmentsPage = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();
    const [groupedRows, setGroupedRows] = useState<GroupedPrincipalRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedPrincipalId, setSelectedPrincipalId] = useState<string | null>(null);
    const [rolePermissions, setRolePermissions] = useState<Record<string, string[]>>({});
    const [confirmRevokeAssignment, setConfirmRevokeAssignment] = useState<RoleAssignment | null>(null);
    const [revokePermissions, setRevokePermissions] = useState<string[]>([]);
    const [loadingRevokePerms, setLoadingRevokePerms] = useState(false);
    const listAssignments = useAssignments(s => s.list);
    const deleteRoleAssignment = useAssignments(s => s.delete);
    const { resources, listResources } = useResources();

    const listUsers = useUsers(s => s.list);
    const listGroups = useGroups(s => s.list);
    const listTenants = useTenants(s => s.list);
    const tenants = useTenants(s => s.tenants);
    const listRoles = useRoles(s => s.list);
    const roles = useRoles(s => s.roles);
    const listPermissions = useRoles(s => s.listPermissions);

    useEffect(() => {
        if (confirmRevokeAssignment) {
            setLoadingRevokePerms(true);
            listPermissions(confirmRevokeAssignment.role_id)
                .then(setRevokePermissions)
                .catch(() => setRevokePermissions([]))
                .finally(() => setLoadingRevokePerms(false));
        } else {
            setRevokePermissions([]);
        }
    }, [confirmRevokeAssignment, listPermissions]);

    useEffect(() => {
        setBreadcrumbs([{ name: 'Security & Compliance' }, { name: 'Assignments' }]);
    }, [setBreadcrumbs]);

    const loadAllData = async () => {
        setLoading(true);
        try {
            const [
                assignmentsData,
                resourcesData,
                usersData,
                groupsData,
                tenantsData,
                rolesData
            ] = await Promise.all([
                listAssignments('/platform'),
                listResources(),
                listUsers(),
                listGroups(),
                listTenants(),
                listRoles()
            ]);

            // Group assignments by principal_id
            const groupsMap: Record<string, RoleAssignment[]> = {};
            assignmentsData.forEach(a => {
                if (!groupsMap[a.principal_id]) {
                    groupsMap[a.principal_id] = [];
                }
                groupsMap[a.principal_id].push(a);
            });

            const rows: GroupedPrincipalRow[] = Object.entries(groupsMap).map(([pId, assigns]) => {
                const firstAssign = assigns[0];
                const pType = firstAssign.principal_type;

                // Resolve User or Group details
                let name = pId;
                let subtext = pType === 'spn' ? 'Service Principal' : 'Group';
                let targetUserId: string | null = null;

                if (pType === 'user') {
                    const matchedUser = usersData.find(u => u.identity_id === pId || u.id === pId);
                    if (matchedUser) {
                        name = matchedUser.name || matchedUser.email;
                        subtext = matchedUser.email;
                        targetUserId = matchedUser.id;
                    } else {
                        subtext = 'User';
                    }
                } else if (pType === 'group') {
                    const matchedGroup = groupsData.find(g => g.id === pId || g.identity_id === pId);
                    if (matchedGroup) {
                        name = matchedGroup.name;
                    }
                }

                // Resolve unique Tenants
                const resolvedTenantsMap: Record<string, { id: string; name: string; logo?: string }> = {};
                assigns.forEach(a => {
                    // Get tenant ID from user if possible, or extract from scope URI
                    let tId = '';
                    if (pType === 'user') {
                        const matchedUser = usersData.find(u => u.identity_id === pId || u.id === pId);
                        if (matchedUser?.tenant_id) tId = matchedUser.tenant_id;
                    }
                    if (!tId && a.scope_id.includes('platform/tenants/')) {
                        const parts = a.scope_id.split('/');
                        const tIdx = parts.indexOf('tenants');
                        if (tIdx !== -1 && parts[tIdx + 1]) {
                            tId = parts[tIdx + 1];
                        }
                    }

                    if (tId) {
                        const matchedTenant = tenantsData.find(t => t.id === tId);
                        if (matchedTenant) {
                            resolvedTenantsMap[tId] = {
                                id: tId,
                                name: matchedTenant.name,
                                logo: matchedTenant.logo_url || undefined
                            };
                        }
                    }
                });
                const resolvedTenants = Object.values(resolvedTenantsMap);

                // Resolve unique Scopes
                const resolvedScopesMap: Record<string, { id: string; name: string; logo?: string; isApp?: boolean }> = {};
                assigns.forEach(a => {
                    const res = resourcesData.find(r => r.uri === a.scope_id);
                    if (res) {
                        const isRoot = res.uri.startsWith('platform/tenants/') && !res.uri.includes('/applications/');
                        let sName = res.uri;
                        let logo = undefined;
                        let isApp = false;

                        if (isRoot) {
                            sName = 'root (Tenant Scope)';
                            // find tenant logo
                            const parts = res.uri.split('/');
                            const tId = parts[parts.length - 1];
                            const tObj = tenantsData.find(t => t.id === tId);
                            logo = tObj?.logo_url || res.metadata?.tenant_logo || undefined;
                        } else if (res.metadata?.application_name) {
                            sName = `App: ${res.metadata.application_name}`;
                            logo = res.metadata.application_logo || undefined;
                            isApp = true;
                        }

                        resolvedScopesMap[a.scope_id] = {
                            id: a.scope_id,
                            name: sName,
                            logo,
                            isApp
                        };
                    } else {
                        resolvedScopesMap[a.scope_id] = {
                            id: a.scope_id,
                            name: a.scope_id
                        };
                    }
                });
                const resolvedScopes = Object.values(resolvedScopesMap);

                // Resolve unique Roles
                const resolvedRolesMap: Record<string, { id: string; name: string }> = {};
                assigns.forEach(a => {
                    const matchedRole = rolesData.find(r => r.id === a.role_id);
                    resolvedRolesMap[a.role_id] = {
                        id: a.role_id,
                        name: matchedRole?.name || a.role_id
                    };
                });
                const resolvedRoles = Object.values(resolvedRolesMap);

                return {
                    principalId: pId,
                    principalType: pType,
                    name,
                    subtext,
                    targetUserId,
                    tenants: resolvedTenants,
                    scopes: resolvedScopes,
                    roles: resolvedRoles,
                    assignments: assigns
                };
            });

            setGroupedRows(rows);
        } catch (e) {
            console.error(e);
            toast.error('Failed to load assignments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAllData();
    }, []);

    const handleRowClick = (row: GroupedPrincipalRow) => {
        setSelectedPrincipalId(row.principalId);

        row.roles.forEach(role => {
            if (!rolePermissions[role.id]) {
                listPermissions(role.id)
                    .then(perms => {
                        setRolePermissions(prev => ({
                            ...prev,
                            [role.id]: perms
                        }));
                    })
                    .catch(() => {});
            }
        });
    };

    const handleRevokeRole = (assignment: RoleAssignment, e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmRevokeAssignment(assignment);
    };

    const executeRevoke = async () => {
        if (!confirmRevokeAssignment) return;
        try {
            await deleteRoleAssignment(confirmRevokeAssignment.id);
            toast.success('Role assignment revoked');
            setConfirmRevokeAssignment(null);
            loadAllData();
        } catch {
            toast.error('Failed to revoke assignment');
        }
    };

    const filteredRows = groupedRows.filter(row =>
        !search ||
        row.name.toLowerCase().includes(search.toLowerCase()) ||
        row.subtext.toLowerCase().includes(search.toLowerCase()) ||
        row.tenants.some(t => t.name.toLowerCase().includes(search.toLowerCase())) ||
        row.scopes.some(s => s.name.toLowerCase().includes(search.toLowerCase())) ||
        row.roles.some(r => r.name.toLowerCase().includes(search.toLowerCase()))
    );

    const selectedRow = groupedRows.find(r => r.principalId === selectedPrincipalId);

    const title = selectedRow ? "Assignment Details" : "Assignments";
    const description = selectedRow
        ? "View role assignments and scopes for the selected principal."
        : "Manage role assignments and grant access to users, groups, or service principals.";
    const action = !selectedRow ? (
        <Button onClick={() => navigate('/m/assignments/create')}>
            New Assignment
        </Button>
    ) : undefined;

    return (
        <PageLayout title={title} description={description} action={action}>
            {loading ? (
                <div className="flex justify-center py-20 bg-white dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-900">
                    <Loader />
                </div>
            ) : selectedRow ? (
                <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
                    {/* Detail Page Header / Back Button */}
                    <div className="flex items-center justify-between">
                        <Button
                            variant="ghost"
                            onClick={() => setSelectedPrincipalId(null)}
                            className="gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white pl-0"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Assignments
                        </Button>
                        <Button onClick={() => navigate('/m/assignments/create')}>
                            New Assignment
                        </Button>
                    </div>

                    {/* Principal Info Card */}
                    <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-900 p-5 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center text-orange-600 font-bold text-lg uppercase shrink-0">
                            {selectedRow.name.charAt(0)}
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white">{selectedRow.name}</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{selectedRow.subtext}</p>
                        </div>
                    </div>

                    {/* Grouped & Detailed Roles */}
                    <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-900 p-5">
                        {(() => {
                            const getTenantIdForAssign = (scopeId: string) => {
                                if (scopeId.includes('platform/tenants/')) {
                                    const parts = scopeId.split('/');
                                    const idx = parts.indexOf('tenants');
                                    if (idx !== -1 && parts[idx + 1]) {
                                        return parts[idx + 1];
                                    }
                                }
                                return 'platform';
                            };

                            const grouped: Record<string, Record<string, RoleAssignment[]>> = {};
                            selectedRow.assignments.forEach(a => {
                                const tId = getTenantIdForAssign(a.scope_id);
                                if (!grouped[tId]) grouped[tId] = {};
                                if (!grouped[tId][a.scope_id]) grouped[tId][a.scope_id] = [];
                                grouped[tId][a.scope_id].push(a);
                            });

                            if (selectedRow.assignments.length === 0) {
                                return (
                                    <div className="py-12 text-center text-slate-400 italic text-sm">
                                        No roles assigned.
                                    </div>
                                );
                            }

                            return (
                                <div className="space-y-8">
                                    {Object.entries(grouped).map(([tenantId, scopesGroup]) => {
                                        const tenantObj = tenants.find(t => t.id === tenantId);
                                        return (
                                            <div key={tenantId} className="space-y-4">
                                                {/* Tenant Header */}
                                                <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-slate-800">
                                                    {tenantObj?.logo_url ? (
                                                        <img src={tenantObj.logo_url} alt="" className="w-5 h-5 rounded object-contain bg-slate-50 dark:bg-slate-800 p-0.5" />
                                                    ) : (
                                                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                                                    )}
                                                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{tenantObj?.name || 'Platform'} Scope</span>
                                                </div>

                                                {/* Scopes Under Tenant */}
                                                <div className="space-y-4 pl-4 border-l border-slate-100 dark:border-slate-800">
                                                    {Object.entries(scopesGroup).map(([scopeId, assignsList]) => {
                                                        const res = resources.find(r => r.uri === scopeId);
                                                        const isRoot = scopeId.startsWith('platform/tenants/') && !scopeId.includes('/applications/');
                                                        let scopeName = scopeId;
                                                        let scopeLogo = undefined;
                                                        let isApp = false;

                                                        if (isRoot) {
                                                            scopeName = 'root (Tenant Scope)';
                                                            scopeLogo = tenantObj?.logo_url || res?.metadata?.tenant_logo || undefined;
                                                        } else if (res?.metadata?.application_name) {
                                                            scopeName = `App: ${res.metadata.application_name}`;
                                                            scopeLogo = res.metadata.application_logo || undefined;
                                                            isApp = true;
                                                        }

                                                        return (
                                                            <div key={scopeId} className="bg-slate-50/20 dark:bg-slate-900/10 rounded-2xl p-4 border border-slate-100 dark:border-slate-800/80">
                                                                {/* Scope Header */}
                                                                <div className="flex items-center gap-2 mb-3">
                                                                    {scopeLogo ? (
                                                                        <img src={scopeLogo} alt="" className="w-4.5 h-4.5 rounded object-contain bg-white dark:bg-slate-950 p-0.5" />
                                                                    ) : isApp ? (
                                                                        <Globe className="w-4 h-4 text-purple-400" />
                                                                    ) : (
                                                                        <Database className="w-4 h-4 text-slate-400" />
                                                                    )}
                                                                    <div>
                                                                        <div className="text-xs font-bold text-slate-900 dark:text-white">{scopeName}</div>
                                                                        <div className="text-[10px] font-mono text-slate-400">{scopeId}</div>
                                                                    </div>
                                                                </div>

                                                                {/* Roles & Permissions */}
                                                                <div className="grid gap-2.5">
                                                                    {assignsList.map(a => {
                                                                        const matchedRole = roles.find(r => r.id === a.role_id);
                                                                        const perms = rolePermissions[a.role_id] || [];
                                                                        return (
                                                                            <div key={a.id} className="flex items-start justify-between p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200/45 dark:border-slate-800/45">
                                                                                <div className="space-y-1.5">
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        <Shield className="w-3.5 h-3.5 text-orange-500" />
                                                                                        <span className="text-xs font-bold font-mono text-slate-850 dark:text-slate-200">{matchedRole?.name || a.role_id}</span>
                                                                                    </div>
                                                                                    <div className="flex flex-wrap gap-1">
                                                                                        {perms.length > 0 ? (
                                                                                            perms.map(p => (
                                                                                                <span key={p} className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[9px] font-mono text-slate-500 dark:text-slate-400">
                                                                                                    {p}
                                                                                                </span>
                                                                                            ))
                                                                                        ) : (
                                                                                            <span className="text-[9px] text-slate-400 italic">No permissions associated</span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    onClick={(e) => handleRevokeRole(a, e)}
                                                                                    className="text-slate-400 hover:text-red-500 p-1.5 shrink-0"
                                                                                >
                                                                                    <Trash2 className="w-4 h-4" />
                                                                                </Button>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            ) : (
                <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-900 overflow-hidden">
                    {/* Toolbar */}
                    <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-900 flex items-center justify-between gap-4">
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Grouped Principals ({filteredRows.length})</h3>
                        <div className="relative">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by user, tenant, scope, or role..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="pl-10 pr-4 py-2 text-sm border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[--primary]/20 focus:border-[--primary] transition-all w-72"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        {filteredRows.length === 0 ? (
                            <div className="p-12 text-center flex flex-col items-center justify-center">
                                <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                                    <Shield className="w-8 h-8 text-slate-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No assignments found</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">Assign roles to users, groups, or service principals to grant access.</p>
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-100 dark:border-slate-900 text-slate-400 dark:text-slate-500 text-left">
                                        <th className="px-6 py-3 font-bold text-[10px] uppercase tracking-widest">User / Principal</th>
                                        <th className="px-6 py-3 font-bold text-[10px] uppercase tracking-widest">Tenant</th>
                                        <th className="px-6 py-3 font-bold text-[10px] uppercase tracking-widest">Scope</th>
                                        <th className="px-6 py-3 font-bold text-[10px] uppercase tracking-widest">Role(s)</th>
                                        <th className="px-6 py-3 font-bold text-[10px] uppercase tracking-widest text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {filteredRows.map(row => (
                                        <tr
                                            key={row.principalId}
                                            onClick={() => handleRowClick(row)}
                                            className={`hover:bg-slate-50/50 dark:hover:bg-slate-900/50 transition-colors cursor-pointer`}
                                        >
                                            <td className="px-6 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center text-orange-600 font-bold text-sm uppercase shrink-0">
                                                        {row.name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-sm text-slate-900 dark:text-white">{row.name}</div>
                                                        <div className="text-xs text-slate-400">{row.subtext}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3">
                                                {row.tenants.length > 0 ? (
                                                    <div className="flex items-center gap-2">
                                                        {row.tenants[0].logo ? (
                                                            <img src={row.tenants[0].logo} alt="" className="w-5 h-5 rounded object-contain bg-slate-50 dark:bg-slate-800 p-0.5 shrink-0" />
                                                        ) : (
                                                            <Building2 className="w-4 h-4 text-slate-400" />
                                                        )}
                                                        <span className="text-slate-700 dark:text-slate-300 font-medium">
                                                            {row.tenants[0].name}
                                                            {row.tenants.length > 1 && (
                                                                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                                                                    +{row.tenants.length - 1} more
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-3">
                                                {row.scopes.length > 0 ? (
                                                    <div className="flex items-center gap-2">
                                                        {row.scopes[0].logo ? (
                                                            <img src={row.scopes[0].logo} alt="" className="w-5 h-5 rounded object-contain bg-slate-50 dark:bg-slate-800 p-0.5 shrink-0" />
                                                        ) : row.scopes[0].isApp ? (
                                                            <Globe className="w-4 h-4 text-purple-400" />
                                                        ) : (
                                                            <Database className="w-4 h-4 text-slate-400" />
                                                        )}
                                                        <span className="text-slate-700 dark:text-slate-300 font-medium">
                                                            {row.scopes[0].name}
                                                            {row.scopes.length > 1 && (
                                                                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                                                                    +{row.scopes.length - 1} more
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-3">
                                                {row.roles.length > 0 ? (
                                                    <div className="flex items-center gap-2">
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 rounded-md text-xs font-mono font-medium">
                                                            <Shield className="w-3 h-3" />
                                                            {row.roles[0].name}
                                                        </span>
                                                        {row.roles.length > 1 && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                                                                +{row.roles.length - 1} more
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-3 text-right">
                                                <span className="text-xs text-orange-500 dark:text-orange-450 font-semibold select-none hover:underline">
                                                    View Details &rarr;
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Confirm Revoke Modal */}
            {confirmRevokeAssignment && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-950 border border-red-200 dark:border-red-900/40 p-6 rounded-2xl w-full max-w-md shadow-2xl">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center shrink-0">
                                <Trash2 className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-slate-900 dark:text-white">Revoke Role Assignment</h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                    Removing <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{confirmRevokeAssignment.role_id}</span> at scope <span className="font-mono text-xs">{confirmRevokeAssignment.scope_id}</span>.
                                </p>
                            </div>
                        </div>

                        <div className="mb-5">
                            <h4 className="text-[11px] font-bold text-red-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                <Shield className="w-3.5 h-3.5" />
                                Permissions That Will Be Lost
                            </h4>
                            {loadingRevokePerms ? (
                                <div className="py-4 flex justify-center"><Loader /></div>
                            ) : revokePermissions.length === 0 ? (
                                <p className="text-xs text-slate-400 italic py-2">No permissions listed for this role.</p>
                            ) : (
                                <div className="max-h-36 overflow-y-auto border border-red-100 dark:border-red-900/30 rounded-xl p-3 bg-red-50/30 dark:bg-red-950/20 divide-y divide-red-100/50 dark:divide-red-900/20">
                                    {revokePermissions.map(p => (
                                        <div key={p} className="py-1.5 text-xs font-mono text-red-600 dark:text-red-400 first:pt-0 last:pb-0">{p}</div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                            <Button variant="ghost" className="flex-1" onClick={() => setConfirmRevokeAssignment(null)}>Cancel</Button>
                            <Button
                                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold"
                                onClick={executeRevoke}
                            >
                                Revoke Access
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </PageLayout>
    );
};
